/**
 * Inventario de farmacia: qué hay, de qué lote y qué está por vencer.
 *
 * Arranca por las ALERTAS y no por la lista: quien abre esta pantalla en un
 * turno normal no viene a leer 300 productos, viene a ver si hay algo vencido,
 * algo por vencer o algo que se está acabando. La lista completa está debajo.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Boxes, Plus, Search, TriangleAlert } from "lucide-react";
import {
  type Lote,
  type Producto,
  type ProductoCreate,
  type TipoAjuste,
  crearAjuste,
  createProducto,
  getAlertas,
  listLotesDeProducto,
  listProductos,
  updateProducto,
} from "../api/farmacia";
import { formatApiError } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  Field,
  Input,
} from "../components/ui";

const soles = (v: string | number | null | undefined) =>
  `S/ ${Number(v ?? 0).toFixed(2)}`;

function EstadoStock({ producto }: { producto: Producto }) {
  if (producto.stock <= 0) return <Badge variant="danger">Sin stock</Badge>;
  if (producto.stock_minimo > 0 && producto.stock <= producto.stock_minimo) {
    return <Badge variant="warning">Bajo mínimo</Badge>;
  }
  return null;
}

export function FarmaciaInventarioPage() {
  const { me } = useAuth();
  const queryClient = useQueryClient();
  const [q, setQ] = useState("");
  const [nuevoOpen, setNuevoOpen] = useState(false);
  const [ajusteLote, setAjusteLote] = useState<Lote | null>(null);
  const [detalle, setDetalle] = useState<Producto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const puedeEditar = me?.permisos.includes("farmacia:producto") ?? false;
  const puedeAjustar = me?.permisos.includes("farmacia:ajuste") ?? false;

  const { data: productos = [], isLoading } = useQuery({
    queryKey: ["farmacia", "productos", q],
    queryFn: () => listProductos({ q: q.trim() || undefined }),
  });
  const { data: alertas } = useQuery({
    queryKey: ["farmacia", "alertas"],
    queryFn: () => getAlertas(),
  });

  const refrescar = () => {
    queryClient.invalidateQueries({ queryKey: ["farmacia"] });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
            <Boxes className="h-6 w-6 text-[color:var(--color-link)]" />
            Inventario
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Catálogo, existencias por lote y vencimientos.
          </p>
        </div>
        {puedeEditar && (
          <Button onClick={() => setNuevoOpen(true)}>
            <Plus className="h-4 w-4" />
            Nuevo producto
          </Button>
        )}
      </div>

      {error && <Alert>{error}</Alert>}

      {/* Lo urgente primero. */}
      {alertas && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <TarjetaAlerta
            titulo="Vencidos"
            tono="danger"
            total={alertas.vencidos.length}
            detalle="No se despachan. Hay que darles de baja."
          >
            {alertas.vencidos.slice(0, 4).map((l) => (
              <LineaLote key={l.id} lote={l} />
            ))}
          </TarjetaAlerta>
          <TarjetaAlerta
            titulo="Por vencer (90 días)"
            tono="warning"
            total={alertas.por_vencer.length}
            detalle="Todavía se pueden rotar o devolver."
          >
            {alertas.por_vencer.slice(0, 4).map((l) => (
              <LineaLote key={l.id} lote={l} />
            ))}
          </TarjetaAlerta>
          <TarjetaAlerta
            titulo="Bajo mínimo"
            tono="warning"
            total={alertas.bajo_minimo.length}
            detalle="Hay que reponer antes de quedarse sin."
          >
            {alertas.bajo_minimo.slice(0, 4).map((a) => (
              <p key={a.producto_id} className="text-xs text-slate-600">
                {a.producto_nombre}{" "}
                <span className="font-mono text-slate-400">
                  {a.stock}/{a.stock_minimo}
                </span>
              </p>
            ))}
          </TarjetaAlerta>
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Search className="h-4 w-4 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nombre comercial, principio activo o código…"
              className="w-full border-0 p-0 text-sm focus:outline-none"
            />
            <span className="shrink-0 text-xs text-slate-400">
              {productos.length} productos
            </span>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {isLoading && (
            <p className="px-6 py-8 text-center text-sm text-slate-400">Cargando…</p>
          )}
          {!isLoading && productos.length === 0 && (
            <p className="px-6 py-10 text-center text-sm text-slate-500">
              Sin productos. Registra el primero para empezar a controlar stock.
            </p>
          )}
          {productos.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-400">
                  <tr>
                    <th className="px-6 py-2 font-medium">Producto</th>
                    <th className="px-3 py-2 font-medium">Presentación</th>
                    <th className="px-3 py-2 text-right font-medium">Precio</th>
                    <th className="px-3 py-2 text-right font-medium">Stock</th>
                    <th className="px-3 py-2 font-medium">Vence</th>
                    <th className="px-6 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {productos.map((p) => (
                    <tr key={p.id} className="hover:bg-slate-50/60">
                      <td className="px-6 py-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-slate-900">{p.nombre}</span>
                          {p.concentracion && (
                            <span className="text-slate-500">{p.concentracion}</span>
                          )}
                          {p.requiere_receta && <Badge variant="neutral">Receta</Badge>}
                          {p.es_controlado && <Badge variant="danger">Controlado</Badge>}
                          <EstadoStock producto={p} />
                        </div>
                        <p className="text-xs text-slate-400">
                          <span className="font-mono">{p.codigo}</span>
                          {p.principio_activo ? ` · ${p.principio_activo}` : ""}
                        </p>
                      </td>
                      <td className="px-3 py-2 text-slate-600">
                        {p.forma_farmaceutica ?? "—"}
                        {p.presentacion ? ` · ${p.presentacion}` : ""}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-slate-700">
                        {soles(p.precio_venta)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-slate-900">
                        {p.stock}
                      </td>
                      <td className="px-3 py-2 text-slate-500">
                        {p.proximo_vencimiento ?? "—"}
                      </td>
                      <td className="px-6 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => setDetalle(p)}
                          className="text-xs font-medium text-[color:var(--color-link)] hover:underline"
                        >
                          Lotes
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {nuevoOpen && (
        <NuevoProductoDialog
          onClose={() => setNuevoOpen(false)}
          onSaved={() => {
            setNuevoOpen(false);
            refrescar();
          }}
          onError={setError}
        />
      )}

      {detalle && (
        <LotesDialog
          producto={detalle}
          puedeEditar={puedeEditar}
          puedeAjustar={puedeAjustar}
          onClose={() => setDetalle(null)}
          onAjustar={(l) => {
            setDetalle(null);
            setAjusteLote(l);
          }}
          onSaved={refrescar}
          onError={setError}
        />
      )}

      {ajusteLote && (
        <AjusteDialog
          lote={ajusteLote}
          onClose={() => setAjusteLote(null)}
          onSaved={() => {
            setAjusteLote(null);
            refrescar();
          }}
          onError={setError}
        />
      )}
    </div>
  );
}

function TarjetaAlerta({
  titulo,
  total,
  detalle,
  tono,
  children,
}: {
  titulo: string;
  total: number;
  detalle: string;
  tono: "danger" | "warning";
  children: React.ReactNode;
}) {
  const vacio = total === 0;
  const color = vacio
    ? "border-slate-200"
    : tono === "danger"
      ? "border-red-200 bg-red-50/50"
      : "border-amber-200 bg-amber-50/50";
  return (
    <div className={`rounded-xl border p-4 ${color}`}>
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          {tono === "danger" ? (
            <TriangleAlert className="h-4 w-4 text-red-600" />
          ) : (
            <AlertTriangle className="h-4 w-4 text-amber-600" />
          )}
          {titulo}
        </h2>
        <span className="font-mono text-lg font-bold text-slate-900">{total}</span>
      </div>
      <p className="mt-0.5 text-xs text-slate-500">{vacio ? "Nada por ahora." : detalle}</p>
      <div className="mt-2 space-y-0.5">{children}</div>
    </div>
  );
}

function LineaLote({ lote }: { lote: Lote }) {
  return (
    <p className="text-xs text-slate-600">
      {lote.producto_nombre}{" "}
      <span className="font-mono text-slate-400">
        {lote.numero} · {lote.fecha_vencimiento ?? "—"} · {lote.cantidad} u.
      </span>
    </p>
  );
}

function NuevoProductoDialog({
  onClose,
  onSaved,
  onError,
}: {
  onClose: () => void;
  onSaved: () => void;
  onError: (m: string | null) => void;
}) {
  const [form, setForm] = useState<ProductoCreate>({
    codigo: "",
    nombre: "",
    principio_activo: "",
    concentracion: "",
    forma_farmaceutica: "",
    presentacion: "",
    unidad: "UNIDAD",
    categoria: "MEDICAMENTO",
    requiere_receta: false,
    es_controlado: false,
    precio_venta: 0,
    stock_minimo: 0,
  });

  const guardar = useMutation({
    mutationFn: () =>
      createProducto({
        ...form,
        principio_activo: form.principio_activo || null,
        concentracion: form.concentracion || null,
        forma_farmaceutica: form.forma_farmaceutica || null,
        presentacion: form.presentacion || null,
        precio_venta: Number(form.precio_venta) || 0,
        stock_minimo: Number(form.stock_minimo) || 0,
      }),
    onSuccess: () => {
      onError(null);
      onSaved();
    },
    onError: (e) => onError(formatApiError(e, "No se pudo crear el producto.")),
  });

  const set = (k: keyof ProductoCreate, v: unknown) => setForm({ ...form, [k]: v });

  return (
    <Dialog
      open
      onClose={onClose}
      title="Nuevo producto"
      description="El stock no se escribe aquí: entra registrando una compra."
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Código" htmlFor="p_codigo">
            <Input
              id="p_codigo"
              value={form.codigo}
              onChange={(e) => set("codigo", e.target.value)}
              placeholder="MED-018"
            />
          </Field>
          <Field label="Nombre comercial" htmlFor="p_nombre">
            <Input
              id="p_nombre"
              value={form.nombre}
              onChange={(e) => set("nombre", e.target.value)}
              placeholder="Paracetamol"
            />
          </Field>
          <Field
            label="Principio activo"
            htmlFor="p_dci"
            hint="El médico receta en genérico; el paciente pide por marca."
          >
            <Input
              id="p_dci"
              value={form.principio_activo ?? ""}
              onChange={(e) => set("principio_activo", e.target.value)}
            />
          </Field>
          <Field label="Concentración" htmlFor="p_conc">
            <Input
              id="p_conc"
              value={form.concentracion ?? ""}
              onChange={(e) => set("concentracion", e.target.value)}
              placeholder="500 mg"
            />
          </Field>
          <Field label="Forma farmacéutica" htmlFor="p_forma">
            <Input
              id="p_forma"
              value={form.forma_farmaceutica ?? ""}
              onChange={(e) => set("forma_farmaceutica", e.target.value)}
              placeholder="Tableta"
            />
          </Field>
          <Field label="Presentación" htmlFor="p_pres">
            <Input
              id="p_pres"
              value={form.presentacion ?? ""}
              onChange={(e) => set("presentacion", e.target.value)}
              placeholder="Caja x 100"
            />
          </Field>
          <Field label="Precio de venta (S/)" htmlFor="p_precio">
            <Input
              id="p_precio"
              type="number"
              step="0.10"
              value={String(form.precio_venta ?? 0)}
              onChange={(e) => set("precio_venta", e.target.value)}
              className="font-mono"
            />
          </Field>
          <Field
            label="Stock mínimo"
            htmlFor="p_min"
            hint="Bajo este número aparece en alertas. 0 = sin alerta."
          >
            <Input
              id="p_min"
              type="number"
              value={String(form.stock_minimo ?? 0)}
              onChange={(e) => set("stock_minimo", e.target.value)}
              className="font-mono"
            />
          </Field>
        </div>

        <div className="flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.requiere_receta}
              onChange={(e) => set("requiere_receta", e.target.checked)}
            />
            Requiere receta
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.es_controlado}
              onChange={(e) => set("es_controlado", e.target.checked)}
            />
            Controlado (psicotrópico)
          </label>
          <label className="flex items-center gap-2">
            Categoría
            <select
              value={form.categoria}
              onChange={(e) => set("categoria", e.target.value)}
              className="rounded-lg border border-slate-300 px-2 py-1 text-sm"
            >
              <option value="MEDICAMENTO">Medicamento</option>
              <option value="INSUMO">Insumo</option>
              <option value="MATERIAL">Material</option>
              <option value="OTRO">Otro</option>
            </select>
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={() => guardar.mutate()}
            disabled={!form.codigo.trim() || !form.nombre.trim() || guardar.isPending}
          >
            Guardar producto
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function LotesDialog({
  producto,
  puedeEditar,
  puedeAjustar,
  onClose,
  onAjustar,
  onSaved,
  onError,
}: {
  producto: Producto;
  puedeEditar: boolean;
  puedeAjustar: boolean;
  onClose: () => void;
  onAjustar: (l: Lote) => void;
  onSaved: () => void;
  onError: (m: string | null) => void;
}) {
  const { data: lotes = [], isLoading } = useQuery({
    queryKey: ["farmacia", "lotes", producto.id],
    queryFn: () => listLotesDeProducto(producto.id),
  });
  const [precio, setPrecio] = useState(String(producto.precio_venta));
  const [minimo, setMinimo] = useState(String(producto.stock_minimo));

  const guardar = useMutation({
    mutationFn: () =>
      updateProducto(producto.id, {
        precio_venta: Number(precio) || 0,
        stock_minimo: Number(minimo) || 0,
      }),
    onSuccess: () => {
      onError(null);
      onSaved();
      onClose();
    },
    onError: (e) => onError(formatApiError(e, "No se pudo actualizar.")),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={producto.nombre}
      description={`${producto.codigo} · stock total ${producto.stock} ${producto.unidad.toLowerCase()}`}
      size="lg"
    >
      <div className="space-y-4">
        {isLoading && <p className="text-sm text-slate-400">Cargando lotes…</p>}
        {!isLoading && lotes.length === 0 && (
          <p className="text-sm text-slate-500">
            Sin lotes. Registra una compra para que entre mercadería.
          </p>
        )}
        {lotes.length > 0 && (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-slate-400">
              <tr>
                <th className="py-1 font-medium">Lote</th>
                <th className="py-1 font-medium">Vence</th>
                <th className="py-1 text-right font-medium">Cantidad</th>
                <th className="py-1 text-right font-medium">Costo</th>
                <th className="py-1" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {lotes.map((l) => (
                <tr key={l.id}>
                  <td className="py-1.5 font-mono text-slate-700">{l.numero}</td>
                  <td className="py-1.5">
                    {l.fecha_vencimiento ?? "—"}
                    {l.vencido && <Badge variant="danger">Vencido</Badge>}
                    {!l.vencido &&
                      l.dias_para_vencer != null &&
                      l.dias_para_vencer <= 90 && (
                        <span className="ml-2 text-xs text-amber-700">
                          en {l.dias_para_vencer} días
                        </span>
                      )}
                  </td>
                  <td className="py-1.5 text-right font-mono">{l.cantidad}</td>
                  <td className="py-1.5 text-right font-mono text-slate-500">
                    {soles(l.costo_unitario)}
                  </td>
                  <td className="py-1.5 text-right">
                    {puedeAjustar && (
                      <button
                        type="button"
                        onClick={() => onAjustar(l)}
                        className="text-xs font-medium text-[color:var(--color-link)] hover:underline"
                      >
                        Ajustar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {puedeEditar && (
          <div className="grid grid-cols-1 gap-4 border-t border-slate-100 pt-4 sm:grid-cols-3">
            <Field label="Precio de venta (S/)" htmlFor="l_precio">
              <Input
                id="l_precio"
                type="number"
                step="0.10"
                value={precio}
                onChange={(e) => setPrecio(e.target.value)}
                className="font-mono"
              />
            </Field>
            <Field label="Stock mínimo" htmlFor="l_min">
              <Input
                id="l_min"
                type="number"
                value={minimo}
                onChange={(e) => setMinimo(e.target.value)}
                className="font-mono"
              />
            </Field>
            <div className="flex items-end">
              <Button onClick={() => guardar.mutate()} disabled={guardar.isPending}>
                Guardar cambios
              </Button>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}

function AjusteDialog({
  lote,
  onClose,
  onSaved,
  onError,
}: {
  lote: Lote;
  onClose: () => void;
  onSaved: () => void;
  onError: (m: string | null) => void;
}) {
  const [tipo, setTipo] = useState<TipoAjuste>("AJUSTE_NEGATIVO");
  const [cantidad, setCantidad] = useState("1");
  const [motivo, setMotivo] = useState("");

  const guardar = useMutation({
    mutationFn: () =>
      crearAjuste({
        lote_id: lote.id,
        tipo,
        cantidad: Number(cantidad) || 0,
        motivo,
      }),
    onSuccess: () => {
      onError(null);
      onSaved();
    },
    onError: (e) => onError(formatApiError(e, "No se pudo registrar el ajuste.")),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="Ajustar existencias"
      description={`${lote.producto_nombre} · lote ${lote.numero} · ${lote.cantidad} unidades`}
    >
      <div className="space-y-4">
        <Field label="Tipo de ajuste" htmlFor="a_tipo">
          <select
            id="a_tipo"
            value={tipo}
            onChange={(e) => setTipo(e.target.value as TipoAjuste)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="AJUSTE_NEGATIVO">Faltante en inventario físico (−)</option>
            <option value="AJUSTE_POSITIVO">Sobrante en inventario físico (+)</option>
            <option value="MERMA">Merma o rotura (−)</option>
            <option value="VENCIMIENTO">Baja por vencimiento (−)</option>
          </select>
        </Field>
        <Field label="Cantidad" htmlFor="a_cant">
          <Input
            id="a_cant"
            type="number"
            min={1}
            value={cantidad}
            onChange={(e) => setCantidad(e.target.value)}
            className="font-mono"
          />
        </Field>
        <Field
          label="Motivo"
          htmlFor="a_motivo"
          hint="Obligatorio. Un ajuste sin explicación es un agujero en el inventario."
        >
          <Input
            id="a_motivo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Inventario del 14/08: faltan 2 blísters"
          />
        </Field>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={() => guardar.mutate()}
            disabled={motivo.trim().length < 3 || guardar.isPending}
          >
            Registrar ajuste
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
